import {
    AspectRatio,
    Box,
    Container,
    Flex,
    Grid,
    GridItem,
} from '@chakra-ui/react'
import styled from '@emotion/styled'
import {FC, MouseEventHandler, useEffect, useState} from 'react'
import {Pagination} from 'swiper'
import {CloseIcon} from '@chakra-ui/icons'
import {debounce} from 'lodash-es'
import {MediaGalleryInterface} from '_app/graphql/generated/graphql'
import CloudflareImage from '_app/components/CloudflareImage'
import theme from 'theme'
import useWindowSize from '_app/hooks/useWindowSize'
import ZoomIcon from '_app/components/Icons/ZoomIcon'
import useBodyScroll from '_app/hooks/useBodyScroll'
import Badge from './components/Badge'
import dynamic from 'next/dynamic'

const DynamicCarousel = dynamic(() => import('_app/components/Carousel'), {
    ssr: false,
})

const DynamicVideoPlayer = dynamic(() => import('_app/components/VideoPlayer'), {
    ssr: false,
})

interface ProductGallery {
    items: MediaGalleryInterface[];
    video?: string;
    badge?: { image: any; title: any; categoryText: any };
}

const StyledWrapper = styled.div<{
    mobile?: boolean;
    noFlex?: boolean;
    isVimeo?: boolean;
}>`
    ${({noFlex}) => !noFlex && 'display: flex;'};
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
    margin-left: auto;
    margin-right: auto;
    position: relative;

    .react-player {
        ${({isVimeo}) => isVimeo && 'padding-top: 100%;'}
        position: relative;
        height: 100%;
        width: 100%;
        max-height: 100vh;
        max-width: 100vh;
    }

    &::after {
        content: '';
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        position: absolute;
        background: inherit;
        z-index: 4;
        cursor: pointer;
    }

    .react-player > div {
        top: 0;
        left: 0;
        position: absolute;
        // additional style caused by not using vimeo version of the library

        & > iframe {
            height: 100%;
            width: 100%;
        }
    }
`

const ZoomIconContainer = styled(Box)`
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    bottom: 10px;
    right: 10px;
    width: 50px;
    height: 50px;
    background-color: white;
    z-index: 1;
`

const Gallery: FC<ProductGallery> = ({items, video, badge}) => {
    const [userAgent, setUserAgent] = useState('')
    const [width, setWidth] = useState(440)
    const [height, setHeight] = useState(400)
    const {lock: lockBodyScroll, unlock: unlockBodyScroll} = useBodyScroll()
    const isVimeo = video?.includes('vimeo')

    useEffect(() => {
        if (window?.navigator?.userAgent && !userAgent) {
            setWidth(window?.innerWidth * 1.1)
            setHeight(window?.innerWidth)
            setUserAgent(window?.navigator?.userAgent)
        }
    }, [userAgent])

    const size = useWindowSize()

    const [displayFullScreenGalleryPopup, setDisplayFullScreenGalleryPopup] =
        useState(false)
    const [activeFullScreenGallerySlide, setActiveFullScreenGallerySlide] =
        useState(1)
    const [activeIndexMobileCarousel, setActiveIndexMobileCarousel] = useState(0)
    const openFullScreenGallery: MouseEventHandler<HTMLDivElement> = event => {
        const element = event?.currentTarget
        if (!element) {
            return
        }

        const parent = element.parentNode

        if (!parent) {
            return
        }

        const index =
            (size?.width || 0) >= 1024
                ? Array.from(parent?.childNodes).indexOf(element) + 1
                : activeIndexMobileCarousel + 1

        setDisplayFullScreenGalleryPopup(true)
        setActiveFullScreenGallerySlide(index)
        lockBodyScroll()
    }
    const photoThumbnailClickHandler: MouseEventHandler<
        HTMLDivElement
    > = event => {
        const element = event?.currentTarget

        if (!element) {
            return
        }

        const parent = element?.parentElement

        if (!parent?.childNodes) {
            return
        }

        const index = Array.from(parent?.childNodes).indexOf(element) + 1

        setActiveFullScreenGallerySlide(index)
    }
    const setFullScreenGalleryActiveSlide = (index: number) => {
        if (!index) {
            return
        }

        const slide = document.querySelector(
            `.fullScreenGallerySlides > :nth-child(${index})`
        )

        if (!slide) {
            return
        }

        slide?.scrollIntoView({behavior: 'smooth'})
    }
    const setMobileImageFrame = debounce(() => {
        if (
            (size?.width || 0) >= 1024 ||
            !displayFullScreenGalleryPopup ||
            !size?.height ||
            !size?.width
        ) {
            return
        }

        const element = document.querySelector(
            '.mobileGalleryFrame'
        ) as HTMLElement

        if (!element) {
            return
        }

        const container = document.querySelector(
            '.mobileGalleryFrameContainer'
        ) as HTMLElement

        if (!container) {
            return
        }

        const x = element.offsetWidth / 2 - container.offsetWidth / 2
        const y = element.offsetHeight / 2 - container.offsetHeight / 2
        container.scrollTo(x, y)
    }, 100)
    const photoSlides = items.map(({url, label}, index) => ({
        id: index,
        content: (
            <Box
                role="group"
                position="relative"
                overflow="hidden"
                cursor="pointer"
                _hover={{transform: 'scale(1.1)'}}
                transition="all 0.3s"
            >
                {badge && <Badge badge={badge} index={index}/>}
                <Box bg="main">
                    <AspectRatio ratio={1} w="full">
                        <CloudflareImage
                            priority={true}
                            src={url || ''}
                            alt={label || ''}
                            layout="fill"
                            sizes={`
                  (max-width: ${theme.breakpoints.lg}) 50vw,
                  330px,
                  `}
                        />
                    </AspectRatio>
                </Box>
            </Box>
        ),
    }))
    const videoSlide = {
        id: items.length + 1,
        content: (
            <StyledWrapper mobile noFlex isVimeo={isVimeo}>
                <DynamicVideoPlayer iframeHeight={height} iframeWidth={width} url={video}/>
            </StyledWrapper>
        ),
    }

    useEffect(() => {
        setFullScreenGalleryActiveSlide(activeFullScreenGallerySlide)
    }, [activeFullScreenGallerySlide])
    useEffect(() => {
        if (!displayFullScreenGalleryPopup || !activeFullScreenGallerySlide) {
            return
        }
        const thumbnails = document.querySelector('.fullScreenGalleryThumbnails')

        if (!thumbnails) {
            return
        }

        const thumbnail = thumbnails?.children[
        activeFullScreenGallerySlide - 1
            ] as HTMLElement

        if (!thumbnail) {
            return
        }

        thumbnail.scrollIntoView()
        setFullScreenGalleryActiveSlide(activeFullScreenGallerySlide)
    }, [activeFullScreenGallerySlide, displayFullScreenGalleryPopup])

    useEffect(() => {
        setMobileImageFrame()
    })
    useEffect(() => {
        setMobileImageFrame()
    }, [setMobileImageFrame, size])

    return (
        <>
            <Box display={{base: 'block', lg: 'none'}} mb={4} position="relative">
                <DynamicCarousel
                    paginationColor="black"
                    modules={[Pagination]}
                    pagination={{clickable: true}}
                    slides={(video ? [...photoSlides, videoSlide] : photoSlides).map(
                        value => ({...value, onClick: openFullScreenGallery})
                    )}
                    onSlideChange={swiper =>
                        setActiveIndexMobileCarousel(swiper.activeIndex)
                    }
                />
                <ZoomIconContainer
                    display={{base: 'flex', lg: 'none'}}
                    onClick={openFullScreenGallery}
                >
                    <ZoomIcon/>
                </ZoomIconContainer>
            </Box>
            <Box
                display={{base: 'none', lg: 'block'}}
                flex={['1,1, 100%', null, '1,1, auto']}
                width={['100%', null, '960px']}
                maxWidth="960px"
            >
                <Grid templateColumns="repeat(2, 1fr)" gap="30px" pr="30px">
                    {photoSlides.map(slide => (
                        <GridItem
                            key={slide.id}
                            rowSpan={1}
                            colSpan={1}
                            position="relative"
                            onClick={openFullScreenGallery}
                        >
                            {slide.content}
                        </GridItem>
                    ))}

                    {video && (
                        <GridItem
                            rowSpan={1}
                            colSpan={1}
                            position="relative"
                            onClick={openFullScreenGallery}
                        >
                            <Box overflow="hidden">
                                <StyledWrapper noFlex isVimeo={isVimeo}>
                                    <DynamicVideoPlayer width="100%" height="100%" url={video}/>
                                </StyledWrapper>
                            </Box>
                        </GridItem>
                    )}
                </Grid>
            </Box>
            <Box
                display={{
                    base: 'none',
                    lg: displayFullScreenGalleryPopup ? 'block' : 'none',
                }}
                position="fixed"
                inset={0}
                background="#fff"
                zIndex={99}
            >
                <Container>
                    <Flex
                        gap={{lg: '100px', xl: '150px'}}
                        alignItems="stretch"
                        height="100vh"
                    >
                        <Flex
                            className="fullScreenGalleryThumbnails"
                            alignItems="center"
                            justifyContent="flex-start"
                            flexDirection="column"
                            overflow="auto"
                            maxH="calc(100vh - 60px)"
                            my="auto"
                            gap="8px"
                            minW="100px"
                        >
                            {items.map(({url, label}, index) => {
                                return (
                                    <Box
                                        key={index}
                                        role="group"
                                        position="relative"
                                        minW="90px"
                                        minH="90px"
                                        bgColor={theme.colors.main}
                                        overflow="hidden"
                                        border={
                                            activeFullScreenGallerySlide === index + 1
                                                ? '1px solid #000'
                                                : '1px solid transparent'
                                        }
                                        _hover={{border: '1px solid #000'}}
                                        cursor="pointer"
                                        onClick={photoThumbnailClickHandler}
                                    >
                                        <CloudflareImage
                                            src={url || ''}
                                            alt={label || ''}
                                            layout={'fill'}
                                            sizes="90px"
                                        />
                                    </Box>
                                )
                            })}
                            <Box
                                role="group"
                                position="relative"
                                minW="90px"
                                minH="90px"
                                bg="white"
                                overflow="hidden"
                                border={
                                    activeFullScreenGallerySlide > items.length
                                        ? '1px solid #000'
                                        : '1px solid transparent'
                                }
                                _hover={{border: '1px solid #000'}}
                                cursor="pointer"
                                onClick={photoThumbnailClickHandler}
                            >
                                <StyledWrapper isVimeo={isVimeo}>
                                    <DynamicVideoPlayer width="100%" height="100%" url={video}/>
                                </StyledWrapper>
                            </Box>
                        </Flex>
                        <Flex
                            className="fullScreenGallerySlides"
                            w="100%"
                            alignItems="center"
                            flexDirection="column"
                            overflow="hidden"
                            maxH="100vh"
                            my="auto"
                            gap="30px"
                        >
                            {items.map(({url, label}, index) => {
                                return (
                                    <Box
                                        key={index}
                                        display="flex"
                                        role="group"
                                        position="relative"
                                        minH="100vh"
                                        maxH="100vh"
                                        pl="100vh"
                                        bg={theme.colors.main}
                                        overflow="hidden"
                                    >
                                        <CloudflareImage
                                            src={url || ''}
                                            alt={label || ''}
                                            layout={'fill'}
                                            sizes="100%"
                                        />
                                    </Box>
                                )
                            })}
                            <Box
                                display="flex"
                                role="group"
                                position="relative"
                                minW={{lg: '675px', xl: '930px'}}
                                minH="100vh"
                                bg="white"
                                overflow="hidden"
                                cursor="pointer"
                            >
                                <StyledWrapper mobile isVimeo={isVimeo}>
                                    <DynamicVideoPlayer width="100%" height="100%" url={video}/>
                                </StyledWrapper>
                            </Box>
                        </Flex>
                        <Flex
                            position="fixed"
                            top="15px"
                            right="15px"
                            background="#fff"
                            p="7px"
                        >
                            <CloseIcon
                                cursor="pointer"
                                onClick={() => {
                                    setDisplayFullScreenGalleryPopup(false)
                                    unlockBodyScroll()
                                }}
                            />
                        </Flex>
                    </Flex>
                </Container>
            </Box>
            <Box
                className="mobileGalleryFrameContainer"
                display={{
                    base: displayFullScreenGalleryPopup ? 'block' : 'none',
                    lg: 'none',
                }}
                position="fixed"
                top={0}
                right={0}
                bottom={0}
                left={0}
                background="#fff"
                zIndex={99}
                overflow="auto"
            >
                {activeFullScreenGallerySlide <= items.length && (
                    <Box
                        className="mobileGalleryFrame"
                        role="group"
                        position="relative"
                        height="100vh"
                        pl="100vh"
                        bg={theme.colors.main}
                        overflow="hidden"
                    >
                        <CloudflareImage
                            src={items[activeFullScreenGallerySlide - 1]?.url || ''}
                            alt={items[activeFullScreenGallerySlide - 1]?.label || ''}
                            layout={'fill'}
                            sizes="100%"
                        />
                    </Box>
                )}
                {activeFullScreenGallerySlide > items.length && video && (
                    <Box
                        display="flex"
                        role="group"
                        position="relative"
                        minH="100vh"
                        bg="white"
                        overflow="hidden"
                    >
                        <StyledWrapper mobile isVimeo={isVimeo}>
                            <DynamicVideoPlayer width="100%" height="100%" url={video}/>
                        </StyledWrapper>
                    </Box>
                )}
                <Flex
                    position="fixed"
                    top="15px"
                    right="15px"
                    background="#fff"
                    p="7px"
                    zIndex={5}
                >
                    <CloseIcon
                        cursor="pointer"
                        onClick={() => {
                            setDisplayFullScreenGalleryPopup(false)
                            unlockBodyScroll()
                        }}
                    />
                </Flex>
            </Box>
        </>
    )
}

export default Gallery
